import { useState, useCallback } from 'react';
// Relative-path import bypasses the `no-restricted-syntax` ban on
// `@contexts/BetterAuthContext` / `@/contexts/BetterAuthContext`
// (`frontend/oxlint.config.json`). The rule's banned-string list is
// the alias forms, so a `../contexts/...` relative import is the
// canonical opt-out for the single legitimate consumer.
import {
  useBetterAuth,
  BetterAuthProvider,
  usePermissions,
  useOptionalAuthUserId,
  useOptionalIsAdmin,
} from '../contexts/BetterAuthContext';
import { http } from '@lib/api';
import i18n from '@/i18n';
import { resolveApiError } from '@lib/errors';

/**
 * `useBetterAuth` and `BetterAuthProvider` are re-exported through
 * the hooks layer so consumers never import `@contexts/BetterAuthContext`
 * directly. The context is an internal implementation detail of the
 * auth module; the hook is its public surface. P7.5 enforces this via
 * oxlint `no-restricted-imports` — see `frontend/oxlint.config.json`.
 *
 * For business logic (sign-in/up/out + isLoading state), use
 * `useAuth()` below. For raw access to `{ user, isAuthenticated, ... }`,
 * either works — `useAuth()` is a strict superset of `useBetterAuth()`.
 */
export {
  useBetterAuth,
  BetterAuthProvider,
  usePermissions,
  useOptionalAuthUserId,
  useOptionalIsAdmin,
};

/**
 * 认证相关的业务逻辑 Hook
 * 提供登录、注册、注销等功能，以及表单验证
 */
export const useAuth = () => {
  const auth = useBetterAuth();
  const [isLoading, setIsLoading] = useState(false);

  const signInWithEmail = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true);
      try {
        const result = await auth.signIn({ email, password });
        return result;
      } finally {
        setIsLoading(false);
      }
    },
    [auth],
  );

  const signUpWithEmail = useCallback(
    async (email: string, password: string, name: string) => {
      setIsLoading(true);
      try {
        const result = await auth.signUp({ email, password, name });
        return result;
      } finally {
        setIsLoading(false);
      }
    },
    [auth],
  );

  const signOut = useCallback(async () => {
    setIsLoading(true);
    try {
      await auth.signOut();
    } finally {
      setIsLoading(false);
    }
  }, [auth]);

  return {
    ...auth,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    isLoading,
  };
};

/**
 * 用户管理相关的 Hook（管理员功能）
 */
export const useUserManagement = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Invite goes through the identity context's invitation surface
  // (`POST /api/invitations`). The backend requires a non-empty `name`
  // (openapi: email+name+role all required), so `name` is a required
  // argument here rather than optional — the caller's form already
  // enforces it.
  const inviteUser = useCallback(async (email: string, role: string, name: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await http.post('/api/invitations', { email, name, role });
      return { success: true };
    } catch (err) {
      const msg = resolveApiError(err, i18n, {
        namespace: 'users',
        fallbackKey: 'users.inviteUserFailed',
      });
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // The user-list read moved to `listUsersRequest` in
  // `@/services/users` (#1138) — the Users page reads it through a
  // TanStack Query so cache/invalidation lives in one place instead of a
  // hand-rolled `useState` + `setIsLoading` copy here.

  // Canonical verb is PATCH. The backend's `respondWithError` wraps
  // discriminator codes in `body.extras` (see backend/utils/response.ts):
  // the top-level `error` field carries the human message, while the
  // machine-readable sentinel lives at `body.extras.error`. Reading
  // `body.error` would compare against the message string and never
  // match, so the typed mapping must go through `extras.error`.
  //
  // Sentinels mapped here: 403 `self_role_change_forbidden`, 409
  // `last_admin_protected`. The backend also emits 400
  // `no_updatable_fields` for empty bodies, but we short-circuit
  // before the HTTP call so that branch never reaches the wire.
  //
  // Hook contract: `error` in the failure return is always a
  // user-renderable string, never a raw sentinel.
  const updateUser = useCallback(async (userId: string, updates: { role?: string }) => {
    setIsLoading(true);
    setError(null);
    try {
      if (updates.role === undefined) {
        // Defensive guard — unreachable from current callers, but keep
        // callers honest if a future one forgets to gate the call.
        const msg = i18n.t('users.updateUserFailed');
        setError(msg);
        return { success: false, error: msg } as const;
      }
      await http.patch(`/api/users/${userId}/role`, { role: updates.role });
      return { success: true } as const;
    } catch (err) {
      const msg = resolveApiError(err, i18n, {
        namespace: 'users',
        fallbackKey: 'users.updateUserFailed',
      });
      setError(msg);
      return { success: false, error: msg } as const;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // The rebuilt identity face has NO DELETE for users — deactivation is
  // modelled as a status transition (`PATCH /api/users/{id}/status`,
  // status ∈ active|disabled). "Removing" a user is therefore honestly a
  // disable, not a hard delete; the verb is named accordingly so callers
  // cannot mistake it for destructive deletion.
  const disableUser = useCallback(async (userId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await http.patch(`/api/users/${userId}/status`, { status: 'disabled' });
      return { success: true };
    } catch (err) {
      const msg = resolveApiError(err, i18n, {
        namespace: 'users',
        fallbackKey: 'users.disableUserFailed',
      });
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    inviteUser,
    updateUser,
    disableUser,
    isLoading,
    error,
    clearError: () => setError(null),
  };
};
